/**
 * Body — structured entries (F2.2 + F2.3): circumferences / skinfolds /
 * InBody under `/v2/stats/body`. Writing for a client (trainer with
 * consent) goes to `…/client/:clientId`; list GET and DELETE are always
 * self. InBody archive uploads in three steps: GET `inbody/signed-upload-url`
 * → PUT the file straight to the bucket → POST `inbody/:id/files` with the
 * filename, which the server writes only over an object it has proved is there.
 */
import type {
  ApiCircumferenceEntryDto,
  ApiAttachInBodyFileDto,
  ApiSkinfoldEntryDto,
  ApiInBodyEntryDto,
} from '@mfw/common';

import type {
  AttachInBodyFileResult,
  ApiCircumferenceEntry,
  FetchBodyEntriesArgs,
  ApiSkinfoldEntry,
  ApiInBodyEntry,
  ApiSignedUrl,
  StatsUploadUrl,
} from './types/statsEntries.types';

import { destroy, post, get } from './axios';

// ——— circumferences ———

export const createCircumference = async ({
  clientId,
  ...dto
}: ApiCircumferenceEntryDto & { clientId?: string }) => {
  const url = clientId
    ? `/v2/stats/body/circumference/client/${clientId}`
    : '/v2/stats/body/circumference';
  const response = await post<ApiCircumferenceEntry>(url, dto);
  // BE returns the raw entity, but the `post` helper types a `{data}` wrapper
  return (response ?? null) as ApiCircumferenceEntry | null;
};

export const fetchCircumference = async (args: FetchBodyEntriesArgs = {}) => {
  const response = await get<ApiCircumferenceEntry[]>(
    '/v2/stats/body/circumference',
    { query: { from: args.from, to: args.to } },
  );
  return response ?? null;
};

// deletes the row AND its explosion into the spine
export const deleteCircumference = async ({ id }: { id: string }) => {
  await destroy(`/v2/stats/body/circumference/${id}`);
};

// ——— skinfolds ———

export const createSkinfold = async ({
  clientId,
  ...dto
}: { clientId?: string } & ApiSkinfoldEntryDto) => {
  const url = clientId
    ? `/v2/stats/body/skinfold/client/${clientId}`
    : '/v2/stats/body/skinfold';
  const response = await post<ApiSkinfoldEntry>(url, dto);
  return (response ?? null) as ApiSkinfoldEntry | null;
};

export const fetchSkinfold = async (args: FetchBodyEntriesArgs = {}) => {
  const response = await get<ApiSkinfoldEntry[]>('/v2/stats/body/skinfold', {
    query: { from: args.from, to: args.to },
  });
  return response ?? null;
};

export const deleteSkinfold = async ({ id }: { id: string }) => {
  await destroy(`/v2/stats/body/skinfold/${id}`);
};

// ——— InBody ———

export const createInBody = async ({
  clientId,
  ...dto
}: { clientId?: string } & ApiInBodyEntryDto) => {
  const url = clientId
    ? `/v2/stats/body/inbody/client/${clientId}`
    : '/v2/stats/body/inbody';
  const response = await post<ApiInBodyEntry>(url, dto);
  return (response ?? null) as ApiInBodyEntry | null;
};

// the list includes `files` (report archive)
export const fetchInBody = async (args: FetchBodyEntriesArgs = {}) => {
  const response = await get<ApiInBodyEntry[]>('/v2/stats/body/inbody', {
    query: { from: args.from, to: args.to },
  });
  return response ?? null;
};

export const deleteInBody = async ({ id }: { id: string }) => {
  await destroy(`/v2/stats/body/inbody/${id}`);
};

/**
 * Step one of the InBody attachment: a presigned PUT and the filename the row
 * will carry. See `progressPhotos.api.ts` for why the upload is three steps.
 */
export const fetchInBodyFileUploadUrl = async (
  mediaType: ApiAttachInBodyFileDto['mediaType'],
) => {
  const response = await get<{ data: StatsUploadUrl }>(
    `/v2/stats/body/inbody/signed-upload-url?mediaType=${encodeURIComponent(mediaType)}`,
  );
  // `response.data` — the presign helper answers `{ data: { url, mediaSource } }`
  // and the `get` wrapper hands back the whole body, exactly as every content
  // presign consumer here already unwraps it (`exercises.api.ts`). Returning
  // `response` made the caller PUT to `undefined`.
  return response?.data ?? null;
};

export const attachInBodyFile = async ({
  entryId,
  ...dto
}: ApiAttachInBodyFileDto & { entryId: string }) => {
  const response = await post<AttachInBodyFileResult>(
    `/v2/stats/body/inbody/${entryId}/files`,
    dto,
  );
  return (response ?? null) as AttachInBodyFileResult | null;
};

/**
 * Presigned read URL for an archived file — called imperatively from a
 * handler (open/download); the URL is short-lived, do not cache it.
 */
export const fetchInBodyFileUrl = async ({ fileId }: { fileId: string }) => {
  const response = await get<ApiSignedUrl>(
    `/v2/stats/body/inbody/files/${fileId}/url`,
  );
  return response ?? null;
};
